<template>
    <Page behind-nav>
        <section>
            <div class="careers-hero">
                <div class="careers-hero__background">
                    <img class="rmnp-hidden" :src="$asset('videos/careers/careers_16x9_poster.jpg')" alt="" />
                    <DynamicVideoSet
                        class="rm-hidden"
                        playsinline
                        muted
                        autoplay
                        loop
                        aria-hidden="true"
                        :poster="$asset('videos/careers/careers_16x9_poster.jpg')"
                        :sources="[
                            { src: $asset('videos/careers/careers_16x9.mp4'), media: '(min-width: 1100px)' },
                            { src: $asset('videos/careers/careers_1x1.mp4'), media: '(min-width: 600px)' },
                            { src: $asset('videos/careers/careers_3x4.mp4') },
                        ]"
                    />
                </div>
                <div class="container">
                    <div class="careers-hero__content"></div>
                </div>
            </div>
        </section>

        <section class="careers-section" id="jobs">
            <div class="careers-positions">
                <div class="container">
                    <div class="careers-positions__copy careers-copy careers-copy--inset">
                        <h1 class="careers-hero__title text-subheading">{{ $t("careers.hero.leader") }}</h1>
                        <p class="text-heading text-heading--md" v-html="$t('careers.positions.heading')"></p>
                    </div>

                    <!-- 
                        <FilterButtons class="careers-positions__filters" :scroll-amount="200" title="Filter by Position">
                            <ProButton
                            v-for="filter in filters"
                            :key="filter.id"
                            @click="changeCategory(filter)"
                            :class="{ 'careers-positions__filter--active': currentFilter.id == filter.id }"
                            pill
                            class="careers-positions__filter"
                            :aria-label="`Filter by '${filter.title}'`"
                            >{{ filter.title }}</ProButton
                            >
                        </FilterButtons>
                    -->

                    <div class="careers-positions__positions-container">
                        <ul v-if="positions.length && !positionsLoadFailed" role="presentation">
                            <li v-for="position in positions" :key="position.id">
                                <PositionListing :position="position" />
                            </li>
                        </ul>
                        <div v-if="!positions.length && !positionsLoadFailed" class="careers-positions__no-results">
                            <p class="text-heading text-heading--xs" v-html="$t('careers.positions.no_results_heading')"></p>
                            <p class="text-body text-body--sm" v-html="$t('careers.positions.no_results_body')"></p>
                        </div>
                        <div v-if="positionsLoadFailed" class="careers-positions__no-results">
                            <p class="text-heading text-heading--xs" v-html="$t('careers.positions.error_results_heading')"></p>
                            <p class="text-body text-body--sm" v-html="$t('careers.positions.error_results_body')"></p>
                        </div>

                        <div class="careers-positions__actions">
                            <ProButton v-if="hasNextPage" :action="loadPositions" pill>Load More</ProButton>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="careers-section">
            <div class="container">
                <div class="careers-columns">
                    <div class="careers-columns__column">
                        <div class="careers-photo-cluster">
                            <div class="careers-photo-cluster__photo" :style="{ backgroundImage: `url('${$asset('images/employment/cluster1.jpg')}')` }"></div>
                            <div class="careers-photo-cluster__photo" :style="{ backgroundImage: `url('${$asset('images/employment/cluster2.jpg')}')` }"></div>
                            <div class="careers-photo-cluster__photo" :style="{ backgroundImage: `url('${$asset('images/employment/cluster3.jpg')}')` }"></div>
                        </div>
                    </div>
                    <div class="careers-columns__column">
                        <div class="careers-columns__copy careers-copy careers-copy--sm-center">
                            <h2 class="text-subheading" id="what-drives-us">{{ $t("careers.drive.leader") }}</h2>
                            <p class="text-heading" v-html="$t('careers.drive.heading')"></p>
                            <p class="text-body" v-html="$t('careers.drive.body')"></p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="careers-section">
            <div class="careers-place">
                <div class="container">
                    <div class="careers-place__slides">
                        <div
                            v-for="(slide, i) in placeSlides"
                            class="careers-place__image"
                            :class="{ 'active-slide': currentPlaceSlide == i }"
                            :style="{ backgroundImage: `url('${$asset(slide)}')` }"
                            :key="i"
                        ></div>
                    </div>
                    <div class="careers-place__copy careers-copy careers-copy--inset careers-copy--sm-center">
                        <h2 id="join-the-journey" class="text-subheading">{{ $t("careers.tassie.leader") }}</h2>
                        <p class="text-heading" v-html="$t('careers.tassie.heading')"></p>
                        <p class="text-body" v-html="$t('careers.tassie.body')"></p>
                    </div>
                </div>
            </div>
        </section>

        <section class="careers-section careers-section--alt">
            <div class="careers-footer">
                <div class="container">
                    <div class="careers-footer__copy careers-copy careers-copy--inset careers-copy--center">
                        <h2 class="text-heading" id="get-in-touch" v-html="$t('careers.footer.heading')"></h2>
                        <div class="careers-footer__contact">
                            <ProButton :href="`mailto:careers@savage.si?subject=${$t('careers.footer.subject')}`" pill variant="primary">{{
                                $t("careers.footer.button")
                            }}</ProButton>
                            <p class="careers-footer__email text-subheading text-subheading--sm">{{ $t("careers.footer.email") }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </Page>
</template>

<script>
import { metaTitles, metaDescriptions, metaImages } from "~/utils/meta";
import { trimI18nHeadLinks, constructi18nHeadMeta } from "~/utils/i18n";
import { mapGetters } from "vuex";
import PositionListing from "@/components/careers/PositionListing";
import FilterButtons from "@/components/FilterButtons";
import DynamicVideoSet from "@/components/DynamicVideoSet.vue";

export default {
    components: {
        PositionListing,
        FilterButtons,
        DynamicVideoSet,
    },

    data: () => {
        const filters = [
            {
                id: "all",
                title: "All Positions",
                query: "",
            },
            {
                id: "product",
                title: "Product",
                query: "Product",
            },
            {
                id: "marketing",
                title: "Marketing",
                query: "Marketing",
            },
            {
                id: "operations",
                title: "Operations",
                query: "Operations",
            },
        ];

        return {
            filters,
            currentFilter: filters[0],

            hasNextPage: false,
            positionsLoadFailed: false,
            paginationAmount: 5,

            placeSlides: ["images/employment/tassie1.jpg", "images/employment/tassie2.jpg", "images/employment/tassie3.jpg"],
            currentPlaceSlide: 0,
            placeSlideTimer: null,
        };
    },

    computed: {
        ...mapGetters({
            positions: "employment/getPositions",
            position: "employment/getPosition",
        }),

        loadAmount() {
            return this.positions.length + this.paginationAmount;
        },
    },

    methods: {
        startPlaceSlideTimer() {
            this.placeSlideTimer = setInterval(() => {
                this.currentPlaceSlide++;

                if (this.currentPlaceSlide >= this.placeSlides.length) {
                    this.currentPlaceSlide = 0;
                }
            }, 5000);
        },

        HandlePositionsLoadResponse(response) {
            this.hasNextPage = response?.next_page_url ? true : false;
            this.positionsLoadFailed = response === undefined;
        },

        async changeCategory(filter) {
            try {
                this.currentFilter = filter;

                const res = await this.$store.dispatch("employment/fetchPositions", { limit: this.paginationAmount, category: this.currentFilter.query });
                this.HandlePositionsLoadResponse(res);
            } catch {
                this.positionsLoadFailed = true;
            }
        },

        async loadPositions() {
            try {
                const res = await this.$store.dispatch("employment/fetchPositions", { limit: this.loadAmount, category: this.currentFilter.query });
                this.HandlePositionsLoadResponse(res);
            } catch {
                this.positionsLoadFailed = true;
            }
        },
    },

    mounted() {
        this.startPlaceSlideTimer();
    },

    async asyncData({ store }) {
        try {
            if (!store.state.employment.positionsLoaded) {
                const res = await store.dispatch("employment/fetchPositions", { limit: 5 });
                const positionsLoadFailed = res === undefined;
                const hasNextPage = res?.next_page_url ? true : false;
                return { positionsLoadFailed, hasNextPage };
            }
        } catch {
            return { positionsLoadFailed: true };
        }
    },

    beforeDestroy() {
        if (this.placeSlideTimer) {
            clearTimeout(this.placeSlideTimer);
        }
    },

    head() {
        const i18nHead = this.$nuxtI18nSeo();

        return {
            title: this.$t("careers.meta.title"),
            meta: [
                ...metaTitles(this.$t("careers.meta.title")),
                ...metaDescriptions(this.$t("careers.meta.description")),
                ...metaImages(this.$asset(this.$t("careers.meta.image"))),
                ...constructi18nHeadMeta(["en"], "en"),
            ],
            link: [...trimI18nHeadLinks(i18nHead.link, ["en"])],
            bodyAttrs: {
                class: "ultra-dark-mode",
            },
        };
    },
};
</script>
